import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  CircularProgress,
  Alert,
  ButtonGroup,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import {
  GoogleMap as GoogleMapComponent,
  InfoWindow,
  MarkerClustererF,
} from "@react-google-maps/api";
import { getMapLoadingStates } from "@/context/MapsLoaderContext";
import { apiGet } from "@/services/genesisApi/httpsMethods";
import { MapCustomControls } from "./MapControls";
import {
  MapMarker,
  ASSET_PIN_DISPLAY_HEIGHT,
  ASSET_PIN_DISPLAY_WIDTH,
} from "./MapMarkers";
import { MapPopUp } from "./MapPopUp";
import type {
  Accordion,
  Sector,
  Country,
  Status,
} from "../../../types/deal";
import "../../../assets/css/GoogleMaps.css";
import { buildSVGIconSimilarAssetsSVGIcon } from "./AssetMapHelperFunctions";
import { CustomMultiSelect } from "@/components/common/inputFields/customMultiSelect";
import { AgGridReact } from "ag-grid-react";
import { HyperLinkRenderer } from "@/utils/hyperlinkRenderer";
import { CustomIconButton } from "@/components/common/customIconButton";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import { numberFormatter } from "@/utils/numberFormatter";

export interface MapCoordinate {
  lat: number;
  lng: number;
  title?: string;
  description?: string;
  id?: string | number;
  // Asset-specific fields for popup
  sector?: string;
  dealName?: string;
  dealId?: number;
  acquisitionPrice?: number;
  valuationAmount?: number;
  totalArea?: number;
  areaType?: string;
  currency?: string;
  category?: string;
  status?: Status;
}

export interface GoogleMapProps {
  /** Single coordinate or array of coordinates to display */
  coordinates?: MapCoordinate | MapCoordinate[];
  /** Train station locations to show as markers */
  trainStations?: MapCoordinate[];
  /** Airport locations to show as markers */
  airports?: MapCoordinate[];
  /** Auto-load nearby train stations from Google Places */
  showNearbyTrainStations?: boolean;
  /** Auto-load nearby airports from Google Places */
  showNearbyAirports?: boolean;
  /** Radius in meters for nearby station/airport search. Default: 15000 */
  nearbySearchRadius?: number;
  /** Map center latitude */
  latitude?: number;
  /** Map center longitude */
  longitude?: number;
  /** Map center. If not provided, will auto-center based on coordinates */
  center?: { lat: number; lng: number };
  /** Map zoom level. Default: 6 */
  zoom?: number;
  /** Map height in pixels or string. Default: 400 */
  height?: number | string;
  /** Map width. Default: 100% */
  width?: string | number;
  /** Width override for container */
  widthOverride?: string;
  /** Height override for container */
  heightOverride?: string;
  /** Whether to show info windows on marker click. Default: true */
  showInfoWindows?: boolean;
  /** Map style options */
  mapOptions?: google.maps.MapOptions;
  /** Emphasize motorways/highways on the basemap */
  highlightMotorways?: boolean;
  /** Cluster asset markers when zoomed out */
  clusterAssets?: boolean;
  /** Zoom threshold below which clustering is enabled */
  clusterMinZoom?: number;
  /** Minimum zoom to show train markers */
  trainMinZoom?: number;
  /** Minimum zoom to show airport markers */
  airportMinZoom?: number;
  /** Custom marker icon for train stations */
  trainStationIcon?: string | google.maps.Icon | google.maps.Symbol;
  /** Custom marker icon for airports */
  airportIcon?: string | google.maps.Icon | google.maps.Symbol;
  /** Callback when a marker is clicked */
  onMarkerClick?: (coordinate: MapCoordinate, index: number) => void;
  /** Callback when the map is loaded */
  onMapLoad?: (map: google.maps.Map) => void;
  /**
   * When true, asset markers cannot be dragged.
   * Defaults to true; set false (e.g. from AssetMap) to allow dragging.
   */
  markersNotMoveable?: boolean;
  /** Callback when marker position changes */
  onMarkerPositionChange?: (position: { lat: number; lng: number }) => void;
  /** Asset name for main marker */
  assetName?: string;
  /**
   * (Deprecated) Callback when places are fetched via API (for caching).
   * This component no longer performs Places API fetching; caching is handled by `AssetMap`.
   */
  onPlacesFetched?: (places: {
    trainStations: MapCoordinate[];
    airports: MapCoordinate[];
  }) => void;
  /** Accordions for filtering assets */
  accordions?: Accordion[];
  /** Selected accordion IDs */
  selectedAccordions?: Set<number>;
  /** Callback when accordion selection changes */
  onAccordionToggle?: (accordionId: number) => void;
  /** Whether to show initial deal assets (assets not in any accordion) */
  showInitialDealAssets?: boolean;
  /** Callback when initial deal assets toggle changes */
  onInitialDealAssetsToggle?: (show: boolean) => void;
  /** Show asset numbers on markers */
  showAssetNumbers?: boolean;
  showSimilarAssets?: boolean;
  dealMapInfo?: {
    sectors: Sector[];
    countries: Country[];
  };
  setMapFullscreenOpen?: (open: boolean) => void;
  mapFullscreenOpen?: boolean;
  hideFullscreenButton?: boolean;
}

const EMPTY_DEAL_MAP_INFO: NonNullable<GoogleMapProps["dealMapInfo"]> = {
  sectors: [],
  countries: [],
};

const GoogleMap: React.FC<GoogleMapProps> = ({
  coordinates,
  trainStations,
  airports,
  showNearbyTrainStations = false,
  showNearbyAirports = false,
  latitude,
  longitude,
  center,
  zoom = 6,
  height = 400,
  width = "100%",
  widthOverride,
  heightOverride,
  showInfoWindows = true,
  mapOptions = {},
  clusterAssets = true,
  clusterMinZoom = 12,
  trainMinZoom = 8,
  airportMinZoom = 8,
  trainStationIcon,
  airportIcon,
  onMarkerClick,
  onMapLoad,
  markersNotMoveable = false,
  onMarkerPositionChange,
  assetName,
  accordions = [],
  selectedAccordions,
  onAccordionToggle,
  showInitialDealAssets = true,
  onInitialDealAssetsToggle,
  showAssetNumbers = true,
  dealMapInfo = EMPTY_DEAL_MAP_INFO,
  setMapFullscreenOpen,
  mapFullscreenOpen,
  hideFullscreenButton = false,
}) => {
  const markersDraggable = !markersNotMoveable;
  const mapRef = useRef<google.maps.Map | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<MapCoordinate | null>(
    null,
  );
  const [mapType, setMapType] = useState<string>("roadmap");
  const [infoWindowKey, setInfoWindowKey] = useState<number>(0);
  const [infoWindowOpen, setInfoWindowOpen] = useState<boolean>(false);
  const [showMarker, setShowMarker] = useState<boolean>(false);
  const [similarAssets, setSimilarAssets] = useState<MapCoordinate[]>([]);

  // Initialize toggles - default to enabled if showNearbyTrainStations/showNearbyAirports is true
  // The cached data check will prevent API calls if data exists, but allow user to toggle
  const [trainToggle, setTrainToggle] = useState<boolean>(false);
  const [similarAssetsToggle, setSimilarAssetsToggle] =
    useState<boolean>(false);

  const [dropDownValue, updateDropDown] = useState<(string | number)[]>([]);

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;

    updateDropDown(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };

  const [airportToggle, setAirportToggle] = useState<boolean>(false);

  // NOTE: Places API fetching has been removed from this component. We keep the toggles purely
  // for display/hide of markers that are passed in via props.
  const [motorwaysToggle, setMotorwaysToggle] = useState<boolean>(false);
  const [clusterAssetsToggle, setClusterAssetsToggle] =
    useState<boolean>(false);
  const [currentZoom, setCurrentZoom] = useState<number | undefined>(zoom);
  // Places API fetching removed; keep constants local to other logic only.

  // Sync toggles when props change (e.g., parent enables/disables fetching)

  useEffect(() => {
    if (!similarAssetsToggle) return;

    const coordsList: MapCoordinate[] =
      coordinates == null
        ? []
        : Array.isArray(coordinates)
          ? coordinates
          : [coordinates];

    if (
      dealMapInfo.sectors.length === 0 &&
      dealMapInfo.countries.length === 0
    ) {
      setSimilarAssets([]);
      return;
    }

    const getAssets = async () => {
      const sectorIds = dealMapInfo.sectors
        .map((sector) => `sectorIds=${sector.id}`)
        .join("&");
      const countryIds = dealMapInfo.countries
        .map((country) => `countryIds=${country.id}`)
        .join("&");
      const res = await apiGet<any[]>(`assets?${sectorIds}&${countryIds}`);
      if (res.success && Array.isArray(res.data)) {
        const nextMarkers: MapCoordinate[] = [];
        for (const asset of res.data) {
          if (
            typeof asset.latitude !== "number" ||
            typeof asset.longitude !== "number"
          ) {
            continue;
          }
          if (coordsList.some((coord) => coord.id === asset.id)) continue;

          nextMarkers.push({
            lat: asset.latitude,
            lng: asset.longitude,
            title: asset.asset_name,
            id: asset.id,
            status: asset.status,
            description: asset.description ?? "",
            sector: asset.sector?.sector ?? "",
            dealName: asset.deal?.deal_name ?? "",
            dealId: asset.deal?.id,
            acquisitionPrice: asset.acquisition_price,
            valuationAmount: asset.valuation_amount,
            totalArea: asset.total_area,
            areaType: asset.area_type,
            currency: asset.currency,
            category: asset.category ?? "Asset",
          });
        }
        setSimilarAssets(nextMarkers);
      }
    };

    void getAssets();
  }, [similarAssetsToggle, dealMapInfo, coordinates]);

  const { filteredSimilarAssets } = useMemo(() => {
    const getAverages = (assets: any) => {
      return assets.reduce(
        (acc: any, asset: any) => {
          return {
            valuationAmount: acc.valuationAmount + asset.valuationAmount,
            acquisitionPrice: acc.acquisitionPrice + asset.acquisitionPrice,
            totalArea: acc.totalArea + asset.totalArea,
          };
        },
        { valuationAmount: 0, acquisitionPrice: 0, totalArea: 0 },
      );
    };

    if (dropDownValue.length === 0) {
      const averages = getAverages(similarAssets);
      return { filteredSimilarAssets: similarAssets, averages: averages };
    }
    const filteredAssets = dropDownValue.flatMap((value) =>
      similarAssets.filter((asset) => {
        const id = asset.status?.id;
        if (id === undefined) return false;
        return value === id || Number(value) === id;
      }),
    );

    const averages = getAverages(filteredAssets);

    return {
      filteredSimilarAssets: filteredAssets,
      averages: averages,
    };
  }, [similarAssets, dropDownValue]);

  const similarAssetsStatuses = useMemo(() => {
    const statuses = similarAssets
      .map((asset) => ({
        id: asset.status?.id,
        status: asset.status?.status,
      }))
      .filter(
        (item): item is { id: number; status: string } =>
          item.id !== undefined && item.status !== undefined,
      );

    return Array.from(
      new Map(
        statuses.map((item) => [`${item.id}-${item.status}`, item]),
      ).values(),
    );
  }, [similarAssets]);

  // Convert coordinates to array format
  const coordinatesArray = coordinates
    ? Array.isArray(coordinates)
      ? coordinates
      : [coordinates]
    : [];

  // Calculate center from props
  const mapCenter = useMemo(() => {
    if (center) return center;
    if (latitude !== undefined && longitude !== undefined) {
      return { lat: latitude, lng: longitude };
    }
    if (coordinatesArray.length === 1) {
      return { lat: coordinatesArray[0].lat, lng: coordinatesArray[0].lng };
    }
    if (coordinatesArray.length > 1) {
      const avgLat =
        coordinatesArray.reduce((sum, coord) => sum + coord.lat, 0) /
        coordinatesArray.length;
      const avgLng =
        coordinatesArray.reduce((sum, coord) => sum + coord.lng, 0) /
        coordinatesArray.length;
      return { lat: avgLat, lng: avgLng };
    }
    // Default center (Sheffield, UK)
    return { lat: 53.3811, lng: -1.4701 };
  }, [center, latitude, longitude, coordinatesArray]);

  const { isLoaded, loadError } = getMapLoadingStates();

  // Handle map click
  const handleMapClick = useCallback(() => {
    setSelectedMarker(null);
    setInfoWindowOpen(false);
  }, [selectedMarker]);

  // Handle marker drag end
  const handleMarkerDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng && onMarkerPositionChange) {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();

        if (newLat !== latitude || newLng !== longitude) {
          onMarkerPositionChange({ lat: newLat, lng: newLng });
        }
      }
    },
    [latitude, longitude, onMarkerPositionChange],
  );

  // NOTE: `nearbySearchRadius` is accepted for backwards compatibility, but unused here.

  // Container style
  const containerStyle = useMemo(
    () => ({
      width:
        widthOverride ||
        (typeof width === "number" ? `${width}px` : width) ||
        "100%",
      height:
        heightOverride ||
        (typeof height === "number" ? `${height}px` : height) ||
        "100%",
      flexGrow: 1,
      display: "flex",
      position: "relative" as const,
    }),
    [width, height, widthOverride, heightOverride],
  );

  const assetLegendEntries = useMemo(() => {
    return coordinatesArray.map((coordinate, index) => ({
      number: index + 1,
      name: coordinate.title || `Location ${index + 1}`,
    }));
  }, [coordinatesArray]);

  // Default symbol icons for transit POIs
  const buildSvgIcon = useCallback(
    (
      url: string,
      sizeOpts?: { width: number; height: number },
    ) => {
      if (!isLoaded || typeof window === "undefined" || !window.google?.maps)
        return undefined;
      const w = sizeOpts?.width ?? 32;
      const h = sizeOpts?.height ?? 50;
      // Default 32×50: native aspect for train/airport SVGs. Optional size for asset pins.
      const size = new window.google.maps.Size(w, h);
      return {
        url,
        scaledSize: size,
        anchor: new window.google.maps.Point(w / 2, h),
      } as google.maps.Icon;
    },
    [isLoaded],
  );

  const buildSVGIconSimilarAssets = useCallback(
    (index: number) => {
      if (!isLoaded || typeof window === "undefined" || !window.google?.maps)
        return undefined;

      return buildSVGIconSimilarAssetsSVGIcon(index);
    },
    [isLoaded],
  );

  const trainIconConfig = useMemo(() => {
    if (trainStationIcon) return trainStationIcon;
    return buildSvgIcon("/TrainStation.svg");
  }, [trainStationIcon, buildSvgIcon]);

  const airportIconConfig = useMemo(() => {
    if (airportIcon) return airportIcon;
    return buildSvgIcon("/Airport.svg");
  }, [airportIcon, buildSvgIcon]);

  // Asset marker icon - will be generated dynamically per marker with assetId
  // Keep this for backwards compatibility when no assetId is provided
  const assetMarkerIcon = useMemo(() => {
    return buildSvgIcon("/AssetEmpty.svg", {
      width: ASSET_PIN_DISPLAY_WIDTH,
      height: ASSET_PIN_DISPLAY_HEIGHT,
    });
  }, [buildSvgIcon]);

  const baseStyles = useMemo(() => {
    const styles: google.maps.MapTypeStyle[] = [
      { featureType: "poi", stylers: [{ visibility: "on" }] },
      { featureType: "transit", stylers: [{ visibility: "off" }] },
    ];

    if (motorwaysToggle) {
      // Use controlled_access to target motorways only, leaving A-roads/arterials unchanged
      styles.push(
        {
          featureType: "road.highway.controlled_access",
          elementType: "geometry",
          stylers: [
            { color: "#000000" }, // black highlight for motorways
            { weight: 1.5 },
          ],
        },
        {
          featureType: "road.highway.controlled_access",
          elementType: "labels.text.fill",
          stylers: [{ color: "#000000" }],
        },
        {
          featureType: "road.highway.controlled_access",
          elementType: "labels.text.stroke",
          stylers: [{ color: "#ffffff" }],
        },
      );
    }

    return styles;
  }, [motorwaysToggle]);

  // Default map options
  const defaultOptions = useMemo(
    () => ({
      zoomControl: false,
      fullscreenControl: false,
      streetViewControl: true,
      panControl: false, // Disable pan control (arrow buttons)
      rotateControl: false, // Disable rotate control
      scaleControl: false, // Disable scale control
      overviewMapControl: false, // Disable overview map control
      cameraControl: false, // Disable camera control
      streetViewControlOptions: {
        position:
          (typeof window !== "undefined" &&
            window.google?.maps?.ControlPosition?.RIGHT_BOTTOM) ||
          9,
      },
      gestureHandling: "cooperative",
      mapTypeControl: false,
      styles: mapOptions.styles
        ? [...baseStyles, ...mapOptions.styles]
        : baseStyles,
      ...mapOptions,
    }),
    [mapOptions, baseStyles],
  );

  const clusterActive = useMemo(() => {
    if (currentZoom === undefined || currentZoom === null) return true;
    return currentZoom < clusterMinZoom;
  }, [currentZoom, clusterMinZoom]);

  // Handle map load
  const onLoad = useCallback(
    (map: google.maps.Map) => {
      setMap(map);
      mapRef.current = map;
      setCurrentZoom(map.getZoom() ?? zoom);

      // Set up street view with basic options
      const streetViewInstance = map.getStreetView();
      streetViewInstance.setOptions({
        addressControl: false,
        enableCloseButton: true,
        fullscreenControl: false,
        linksControl: true,
        panControl: true,
        zoomControl: true,
      });

      // Set map bounds based on markers or use default center/zoom
      if (coordinatesArray.length === 0) {
        map.setZoom(zoom);
        map.setCenter(mapCenter);
      } else if (coordinatesArray.length === 1) {
        map.setZoom(zoom);
        map.setCenter({
          lat: coordinatesArray[0].lat,
          lng: coordinatesArray[0].lng,
        });
      } else {
        const bounds = new google.maps.LatLngBounds();
        coordinatesArray.forEach((coord) => {
          bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
        });
        map.fitBounds(bounds);
      }

      if (onMapLoad) {
        onMapLoad(map);
      }
    },
    [coordinatesArray, mapCenter, zoom, onMapLoad],
  );

  // Handle map unmount
  const onUnmount = useCallback(() => {
    setMap(null);
    mapRef.current = null;
  }, []);

  // Handle marker click
  const handleMarkerClick = useCallback(
    (coordinate: MapCoordinate, index: number) => {
      setInfoWindowKey((prevKey) => prevKey + 1);

      if (selectedMarker?.id === coordinate.id) {
        setSelectedMarker(null);
        setInfoWindowOpen(false);
      } else {
        setSelectedMarker(coordinate);
        setInfoWindowOpen(true);

        if (map) {
          map.panTo({ lat: coordinate.lat, lng: coordinate.lng });
        }
      }

      if (onMarkerClick) {
        onMarkerClick(coordinate, index);
      }
    },
    [selectedMarker, map, onMarkerClick],
  );

  // Handle info window close
  const handleInfoWindowClose = useCallback(() => {
    setSelectedMarker(null);
    setInfoWindowOpen(false);
  }, []);

  // Show main marker if latitude and longitude are set
  useEffect(() => {
    if (
      latitude !== null &&
      latitude !== undefined &&
      longitude !== null &&
      longitude !== undefined
    ) {
      setShowMarker(true);
    } else {
      setShowMarker(false);
    }
  }, [latitude, longitude]);

  // Cleanup on unmount
  // Cleanup effect is no longer needed since we removed street view listener

  const columnDefs = useMemo(() => {
    const cols = [
      {
        field: "status",
        headerName: "Status",
        editable: false,
        width: 150,
        cellRenderer: (params: any) => {
          return <>{params.data?.status?.status}</>;
        },
      },
      {
        field: "title",
        headerName: "Asset",
        editable: false,
        width: 150,
        cellRenderer: HyperLinkRenderer("Assets"),
      },
      {
        field: "dealName",
        headerName: "Deal",
        editable: false,
        width: 150,
        cellRenderer: HyperLinkRenderer("deals"),
      },
      {
        field: "valuationAmount",
        headerName: "Valuation Amount",
        valueFormatter: numberFormatter,
        editable: false,
        width: 150,
      },
      {
        field: "acquisitionPrice",
        headerName: "Acquisition Price",
        valueFormatter: numberFormatter,
        editable: false,
        width: 150,
      },
      {
        field: "totalArea",
        headerName: "Total Area",
        valueFormatter: numberFormatter,
        editable: false,
        width: 150,
      },
    ];

    // Deal column - read-only (no editing in table)
    // Accordion column - always show, read-only (editing handled in ExtractedAssetsTable)

    // Add all other columns

    return cols;
  }, []);

  if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
    return (
      <Box
        sx={{
          height,
          width,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Google Maps API key not found. Please add VITE_GOOGLE_MAPS_API_KEY to
          your environment variables.
        </Alert>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          height: containerStyle.height,
          width: containerStyle.width,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Failed to load Google Maps.
        </Alert>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: containerStyle.height,
          width: containerStyle.width,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="google-map-container">
      <div
        style={{
          position: "relative",
          width: similarAssetsToggle ? "50%" : "100%",
          height: "100%",
        }}
      >
        <GoogleMapComponent
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={zoom}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          onZoomChanged={() => {
            const z = mapRef.current?.getZoom();
            if (z !== undefined && z !== null) {
              setCurrentZoom(z);
            }
          }}
          options={defaultOptions}
        >
          {/* POI toggle key */}

          <Box
            sx={{
              position: "absolute",
              top: 10,
              // Leave room for any top-right overlays (e.g. fullscreen button on Deal page)
              right: similarAssetsToggle ? 40 : 70,
              zIndex: 2,
              display: "flex",
              gap: 1,
              pointerEvents: "auto",
            }}
          >
            <ButtonGroup size="small" variant="contained">
              <Button
                color={trainToggle ? "primary" : "inherit"}
                onClick={() => setTrainToggle((prev) => !prev)}
                disabled={!showNearbyTrainStations}
              >
                Trains
              </Button>
              <Button
                color={airportToggle ? "secondary" : "inherit"}
                onClick={() => setAirportToggle((prev) => !prev)}
                disabled={!showNearbyAirports}
              >
                Airports
              </Button>
              <Button
                color={motorwaysToggle ? "primary" : "inherit"}
                onClick={() => setMotorwaysToggle((prev) => !prev)}
              >
                Motorways
              </Button>
              <Button
                sx={{
                  backgroundColor: clusterAssetsToggle ? "#1B7A63" : undefined,
                  "&:hover": {
                    backgroundColor: clusterAssetsToggle
                      ? "#16614F"
                      : undefined,
                  },
                }}
                color={clusterAssetsToggle ? "primary" : "inherit"}
                onClick={() => setClusterAssetsToggle((prev) => !prev)}
              >
                Cluster Assets
              </Button>
              {dealMapInfo.sectors.length > 0 && (
                <Button
                  color={similarAssetsToggle ? "secondary" : "inherit"}
                  onClick={() => setSimilarAssetsToggle((prev) => !prev)}
                  disabled={!showNearbyTrainStations}
                >
                  Show Other Assets
                </Button>
              )}
            </ButtonGroup>
          </Box>

          {!hideFullscreenButton && (
            <Box
              sx={{
                position: "absolute",
                top: 5,
                // Leave room for any top-right overlays (e.g. fullscreen button on Deal page)
                right: mapFullscreenOpen && !similarAssetsToggle ? 10 : 0,
                zIndex: 2,
                display: "flex",
                gap: 1,
                pointerEvents: "auto",
              }}
            >
              <CustomIconButton
                icon={mapFullscreenOpen ? <CloseIcon /> : <FullscreenIcon />}
                onClick={() => {
                  setMapFullscreenOpen?.(!mapFullscreenOpen);
                }}
                data-position="left"
              />
            </Box>
          )}

          {similarAssetsToggle && (
            <div
              style={{
                position: "absolute",
                top: 55,
                // Leave room for any top-right overlays (e.g. fullscreen button on Deal page)
                right: similarAssetsToggle ? 10 : 70,
                zIndex: 2,
                display: "flex",
                gap: 1,
              }}
            >
              <CustomMultiSelect
                value={dropDownValue}
                onChange={handleChange}
                options={similarAssetsStatuses}
                labelKey="status"
                placeholder="Select Status"
                minWidth="200px"
                dropDownWidth="200px"
                useSVGIcons={true}
              />
            </div>
          )}

          {/* Accordion Filter Checkboxes */}
          {accordions.length > 0 && onAccordionToggle && selectedAccordions && (
            <Box
              sx={{
                position: "absolute",
                bottom: 15, // Position below the ButtonGroup
                left: 10,
                zIndex: 2,
                bgcolor: "rgba(255,255,255,0.92)",
                borderRadius: 1,
                boxShadow: 3,
                p: 1.5,
                minWidth: 200,
                maxWidth: 300,
                maxHeight: "66.666%",
                display: "flex",
                flexDirection: "column",
                pointerEvents: "auto",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600, fontSize: 12, flexShrink: 0 }}
              >
                Filter by Addon:
              </Typography>
              <FormGroup
                sx={{
                  gap: 0.5,
                  overflowY: "auto",
                  minHeight: 0,
                  flex: 1,
                }}
              >
                <FormControlLabel
                  sx={{
                    m: 0,
                    alignItems: "center",
                    flexShrink: 0,
                    "& .MuiCheckbox-root": { p: 0, mr: 1 },
                  }}
                  control={
                    <Checkbox
                      checked={showInitialDealAssets ?? true}
                      onChange={(e) =>
                        onInitialDealAssetsToggle?.(e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      Initial Deal Assets
                    </Typography>
                  }
                />
                {accordions
                  .filter(
                    (accordion) => accordion.accordion_name !== "Initial Deal",
                  )
                  .map((accordion) => (
                    <FormControlLabel
                      key={accordion.id}
                      sx={{
                        m: 0,
                        alignItems: "center",
                        flexShrink: 0,
                        "& .MuiCheckbox-root": { p: 0, mr: 1 },
                      }}
                      control={
                        <Checkbox
                          checked={
                            selectedAccordions?.has(accordion.id) ?? false
                          }
                          onChange={() => onAccordionToggle?.(accordion.id)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          {accordion.accordion_name ||
                            `Addon ${accordion.id}`}
                        </Typography>
                      }
                    />
                  ))}
              </FormGroup>
            </Box>
          )}

          <MapCustomControls
            mapType={mapType}
            onChangeMapType={(type: string) => {
              setMapType(type);
              if (map) {
                map.setMapTypeId(type as google.maps.MapTypeId);
              }
            }}
            onZoomIn={() => {
              if (map) {
                const currentZoom = map.getZoom();
                if (currentZoom !== undefined) {
                  map.setZoom(currentZoom + 1);
                }
              }
            }}
            onZoomOut={() => {
              if (map) {
                const currentZoom = map.getZoom();
                if (currentZoom !== undefined) {
                  map.setZoom(currentZoom - 1);
                }
              }
            }}
          />

          {/* Train stations */}
          {trainToggle && (!currentZoom || currentZoom >= trainMinZoom) && (
            <>
              {(trainStations || []).map((station, index) => (
                <MapMarker
                  key={`train-${station.id ?? `${station.lat}-${station.lng}`}-${index}`}
                  latitude={station.lat}
                  longitude={station.lng}
                  title={station.title || "Train station"}
                  icon={trainIconConfig}
                  onMarkerClick={() => handleMarkerClick(station, index)}
                />
              ))}
            </>
          )}

          {similarAssetsToggle && similarAssets.length > 0 && (
            <>
              {filteredSimilarAssets.map((coordinate, index) => (
                <MapMarker
                  key={`${coordinate.lat}-${coordinate.lng}-${index}`}
                  latitude={coordinate.lat}
                  longitude={coordinate.lng}
                  title={coordinate.title}
                  zIndex={99999}
                  icon={buildSVGIconSimilarAssets(coordinate.status?.id ?? 0)}
                  onMarkerClick={() => handleMarkerClick(coordinate, index)}
                />
              ))}
            </>
          )}

          {/* Airports */}
          {airportToggle && (!currentZoom || currentZoom >= airportMinZoom) && (
            <>
              {(airports || []).map((airport, index) => (
                <MapMarker
                  key={`airport-${airport.id ?? `${airport.lat}-${airport.lng}`}-${index}`}
                  latitude={airport.lat}
                  longitude={airport.lng}
                  title={airport.title || "Airport"}
                  icon={airportIconConfig}
                  onMarkerClick={() => handleMarkerClick(airport, index)}
                />
              ))}
            </>
          )}

          {/* Render all markers using MapMarker component */}
          {clusterAssets && clusterAssetsToggle && clusterActive ? (
            <MarkerClustererF>
              {(clusterer) => (
                <>
                  {coordinatesArray.map((coordinate, index) => (
                    <MapMarker
                      key={`${coordinate.lat}-${coordinate.lng}-${index}`}
                      latitude={coordinate.lat}
                      longitude={coordinate.lng}
                      title={coordinate.title || `Location ${index + 1}`}
                      {...(showAssetNumbers
                        ? { assetIndex: index + 1, assetId: coordinate.id }
                        : {})}
                      zIndex={99999}
                      clusterer={clusterer}
                      onMarkerClick={() => handleMarkerClick(coordinate, index)}
                      draggable={markersDraggable}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  ))}
                </>
              )}
            </MarkerClustererF>
          ) : (
            coordinatesArray.map((coordinate, index) => (
              <MapMarker
                key={`${coordinate.lat}-${coordinate.lng}-${index}`}
                latitude={coordinate.lat}
                longitude={coordinate.lng}
                title={coordinate.title || `Location ${index + 1}`}
                {...(showAssetNumbers
                  ? { assetIndex: index + 1, assetId: coordinate.id }
                  : {})}
                zIndex={99999}
                onMarkerClick={() => handleMarkerClick(coordinate, index)}
                draggable={markersDraggable}
                onDragEnd={handleMarkerDragEnd}
              />
            ))
          )}

          {/* Render main asset marker if latitude/longitude provided */}
          {showMarker && latitude !== undefined && longitude !== undefined && (
            <MapMarker
              latitude={latitude}
              longitude={longitude}
              title={assetName || "Asset Location"}
              assetName={assetName}
              draggable={markersDraggable}
              icon={assetMarkerIcon}
              zIndex={99999}
              onDragEnd={handleMarkerDragEnd}
              onMarkerClick={() => {
                const assetCoordinate: MapCoordinate = {
                  lat: latitude,
                  lng: longitude,
                  title: assetName || "Asset Location",
                  id: "asset-marker",
                };
                handleMarkerClick(assetCoordinate, -1);
              }}
            />
          )}

          {/* Info Window */}
          {selectedMarker && infoWindowOpen && showInfoWindows && (
            <InfoWindow
              key={infoWindowKey}
              position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
              onCloseClick={handleInfoWindowClose}
            >
              <MapPopUp
                data={{
                  name: selectedMarker.title,
                  category: selectedMarker.category,
                  sector: selectedMarker.sector,
                  dealName: selectedMarker.dealName,
                  dealId: selectedMarker.dealId,
                  acquisitionPrice: selectedMarker.acquisitionPrice,
                  valuationAmount: selectedMarker.valuationAmount,
                  totalArea: selectedMarker.totalArea,
                  areaType: selectedMarker.areaType,
                  currency: selectedMarker.currency,
                  distance: (selectedMarker as any).distance,
                  travelTime: (selectedMarker as any).travelTime,
                }}
              />
            </InfoWindow>
          )}
        </GoogleMapComponent>

        {showAssetNumbers && assetLegendEntries.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 3,
              backgroundColor: "rgba(255,255,255,0.92)",
              borderRadius: 1,
              boxShadow: 3,
              p: 1.5,
              minWidth: 180,
              maxWidth: 260,
              maxHeight: "66.666%",
              display: "flex",
              flexDirection: "column",
              pointerEvents: "auto",
            }}
          >
            <Box
              sx={{
                fontWeight: 700,
                fontSize: 12,
                mb: 0.75,
                color: "#333",
                flexShrink: 0,
              }}
            >
              Assets
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                overflowY: "auto",
                minHeight: 0,
                flex: 1,
              }}
            >
              {assetLegendEntries.map((entry) => (
                <Box
                  key={entry.number}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: 12,
                    color: "#1f1f1f",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "#800000",
                      color: "#fff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {entry.number}
                  </Box>
                  <Box
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                    title={entry.name}
                  >
                    {entry.name}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </div>
      {similarAssetsToggle && (
        <div className="ag-theme-alpine" style={{ width: "50%" }}>
          <AgGridReact
            rowData={filteredSimilarAssets}
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true }}
            rowHeight={30}
            getRowId={({ data }) => data?.id?.toString()}
          />
        </div>
      )}
    </div>
  );
};

export default GoogleMap;
